import { getEntitySubdomain, selectCurrentEntity } from '@opengov/app-core/entity';
import { getNumericalEntityId } from '@opengov/app-core/entity/selectors';
import { getSessionUUID } from '@opengov/app-core/session/selectors';
import { ValidationStates } from '@opengov/component-library/dist';
import { modalActionCreators } from '@opengov/component-library-classic/module/modals/modalState';
import { selectIsModalOpen } from '@opengov/component-library-classic/module/view';
import { IRecordOf } from '@opengov/compose-app/immutable';
import { Map } from 'immutable';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import { IEntity } from '@opengov/api-deprecated';
import {
  IConditionalReportColumn,
  IGrantee,
  IReport,
  IReportResponse,
  IReportTarget,
  ISavedView
} from '@opengov/reporting-types';
import {
  EOgqlInfixColumnOperators,
  EReportColumnTypes,
  IInfixReportColumn,
  IReportColumn
} from '@opengov/reporting-types/src/reportColumns-immer';
import { produce } from 'immer';

import { reportAccessEffects } from '../../../../module/effects';
import {
  reportGalleryPathReactRouterVersion,
  reportSavedViewSuffix,
  reportSavedViewsPathReactRouterVersion
} from '../../../../module/routes/paths';
import { selectControlBlockProps } from '../../../dimensionalTable/selectors/selectDimensionalTableProps';
import { dimensionalTableActionCreators } from '../../../dimensionalTable/state';
import { ControlBlockStatus } from '../../../dimensionalTable/types';
import {
  selectNewReport,
  selectNewReportFormDescriptionValidationState,
  selectNewReportFormTitleValidationState,
  selectNewReportFormValidationState,
  selectNewReportTypeState
} from '../../../reportCreate/selectors';
import { reportCreateActionCreators } from '../../../reportCreate/state';
import { reportExplorerActionCreators } from '../../../reportExplorer/state';
import {
  selectCurrentGrantees,
  selectInvitableUsers,
  selectTemporaryGrantees
} from '../../../reportExplorer/ReportAccess/selectors/selectReportAccess';
import { accessTableActionCreators } from '../../../reportExplorer/ReportAccess/state/reportAccessTableState';
// tslint:disable-next-line:max-line-length
import { selectSelectedAgGridColumn } from '../../../reportExplorer/ReportDataTableAgGrid/selectors/selectSelectedAgGridColumn';
import { Filter } from '../../../reportExplorer/ReportExportDialog/ReportExportDialog';
import { selectCategorizedByColumn } from '../../../reportExplorer/selectors/currentCategorizedBySelectors';
import { selectCanEditSavedViews } from '../../../reportExplorer/selectors/selectReportExplorerPermissions';
import {
  currentOrganizedBySelectors,
  selectColumnAliasesFromDataMgr,
  selectColumnByNameFromCurrentView,
  selectColumnDisplayName,
  selectCurrentViewExportable,
  selectCurrentViewMaxExportRows,
  selectDetailTableColumns
} from '../../../reportExplorer/selectors/selectReportExplorerState';
import { selectHasReportTarget } from '../../../reportSavedViewEdit/components/AnalysisControls/selectors';
import {
  selectCurrentSavedViewEditSavedView,
  selectSavedViewEditActiveCategory
} from '../../../reportSavedViewEdit/selectors/selectReportSavedViewEdit';
import { basicCalculationEffects } from '../../basicCalculation/effects';
import {
  selectBasicCalculation,
  selectCalculationPreviewTableProps,
  selectOperationOptions,
  selectValue1Options,
  selectValue2Options
} from '../../basicCalculation/selectors';
import { basicCalculationActionCreators } from '../../basicCalculation/state';
import { conditionalEffects } from '../../createConditional/effects';
import {
  selectConditional,
  selectConditionalOperatorOptions,
  selectConditionalPreviewTableProps,
  selectCurrentConditionSetup,
  selectIfColumnOptions,
  selectOptionalSecondValueToCompareOptions,
  selectValueToCompareOptions
} from '../../createConditional/selectors';
import { conditionalActionCreators } from '../../createConditional/state';
import { reportHistoryActionCreators } from '../../history/state';
import { reportLocaleText, selectLocaleFormatter } from '../../locale';
import { reportEffects } from '../../report/effects';
import {
  selectCurrentReport,
  selectCurrentReportTitle,
  selectIsReportDimensional,
  selectIsReportMultiDataset
} from '../../report/selectors';
import { reportConfigurationEffects } from '../../reportConfiguration/effects';
import {
  selectColumnNames,
  selectCurrentFilters,
  selectHiddenControls,
  selectShowColumn
} from '../../reportConfiguration/selectors';
import { reportConfigurationActionCreators } from '../../reportConfiguration/state';
import {
  reportTargetActionCreators,
  selectReportTargetCondition,
  selectReportTargetElseStatus,
  selectSecondReportTargetCondition
} from '../../reportTarget';
import { selectCurrentTemplate } from '../../reportTemplate/standardTemplateSelectors';
import { savedViewEffects } from '../../savedViews';
import { selectSavedViews } from '../../savedViews/selectors';
import {
  IColumnSettingsModal,
  IDeletionBlockingReportControls
} from '../components/ColumnSettingsModal/ColumnSettingsModal';
import { ICreateBasicCalculationModal } from '../components/CreateBasicCalculationModal/CreateBasicCalculationModal';
import { ICreateConditionalModal } from '../components/CreateConditionalModal/CreateConditionalModal';
import { ISavedViewTitleAndDescription } from '../components/SavedViewCreatingModal/SavedViewCreatingModal';
import { ISetTargetModalProps } from '../components/SetTargetModal/SetTargetModal';
import { shareLinkActionCreators, shareLinkSelector } from '../components/ShareModal/state';
import { ReportModalTypes } from '../types/modalTypes';
import { NavigateFunction } from 'react-router-dom';
import { reportActionCreators } from '../../report/state';
import { ToastProps } from '../../../../src/components/Toast/Toast';
import { queryClient } from '../../../../src/apiHooks/core/queryClient';
import { foldersAndReportsQueryKeys } from '../../../../src/pages/reportsPage/hooks/useGetFoldersAndReports/foldersAndReports.queryKeys';
import { MuiToastTypes } from '../../helpers/utils';
import { selectRouteName } from '../../routes/selectors/routeSelectors';
import { reportSavedViewEditActionCreators } from '../../../reportSavedViewEdit/state';

export const selectReportCreatingModalState = createSelector(reportLocaleText, (localeText) => ({
  reportLocaleText: localeText
}));

export const selectCanEditSavedViewsAndOnViewEdit = (canEditSavedViews: boolean, routeName: string): boolean =>
  canEditSavedViews && routeName === 'ReportSavedViewEdit';

export const selectCalculationType = (type: string) => {
  switch (type) {
    case 'calculate':
      modalActionCreators.setModalComponentType(ReportModalTypes.CREATE_BASIC_CALCULATION);
      break;
    case 'conditional':
      modalActionCreators.setModalComponentType(ReportModalTypes.CREATE_CONDITIONAL);
      break;
    default:
      modalActionCreators.setModalComponentType(ReportModalTypes.CREATE_CALCULATION);
      break;
  }
};

export const selectCreateCalculationModalState = createStructuredSelector({
  reportLocaleText: reportLocaleText,
  userCanCreateBasicCalculation: createSelector(
    selectCanEditSavedViews,
    selectRouteName,
    selectCanEditSavedViewsAndOnViewEdit
  ),
  userCanCreateConditional: createSelector(
    selectCanEditSavedViews,
    selectRouteName,
    selectCanEditSavedViewsAndOnViewEdit
  ),
  onCalculationTypeSelect: () => selectCalculationType,
  closeModal: () => () => modalActionCreators.resetModalState()
});

export const selectCreateBasicCalculationModalState = createSelector(
  reportLocaleText,
  selectCalculationPreviewTableProps,
  selectValue1Options,
  selectValue2Options,
  selectBasicCalculation,
  selectDetailTableColumns,
  selectOperationOptions,
  (
    localeText,
    calculationPreviewTableProps,
    value1Options,
    value2Options,
    basicCalculation,
    columns,
    operationOptions
  ): ICreateBasicCalculationModal => ({
    reportLocaleText: localeText,
    value1Options,
    value2Options,
    calculationPreviewTableProps,
    basicCalculation,
    columns,
    operationOptions,
    closeModal: () => {
      modalActionCreators.resetModalState();
      basicCalculationActionCreators.resetBasicCalculation();
    },
    goBack: () => {
      modalActionCreators.setModalComponentType(ReportModalTypes.CREATE_CALCULATION);
      basicCalculationActionCreators.resetBasicCalculation();
    },
    fetchPreviewData: async () => basicCalculationEffects.getCalculationPreviewTableData(),
    updateValue1: (column: IReportColumn) => basicCalculationActionCreators.updateValue1(column),
    updateValue1CustomValue: (value: string) => basicCalculationActionCreators.updateValue1CustomValue(value),
    updateValue2: (column: IReportColumn) => basicCalculationActionCreators.updateValue2(column),
    updateValue2CustomValue: (value: string) => basicCalculationActionCreators.updateValue2CustomValue(value),
    updateOperator: (operator: EOgqlInfixColumnOperators) => basicCalculationActionCreators.updateOperator(operator),
    updateColumnName: (name: string) => basicCalculationActionCreators.updateColumnName(name),
    saveColumn: (column: IInfixReportColumn, navigate: NavigateFunction) => {
      reportConfigurationActionCreators.addBasicCalculatedColumnToDetailTable({ newColumn: column });
      reportConfigurationEffects
        .createReportConfigurationAndUpdateUrl({ navigate })
        .then(() => {
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.SUCCESS,
            headerText: localeText.calculations.basicCalculationModal.createColumnSuccessToastHeader,
            bodyText: localeText.calculations.basicCalculationModal.createColumnSuccessToastMessage,
            dataTest: `toast-calculated-column-sucess`
          });
        })
        .catch(() => {
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.FAILURE,
            headerText: localeText.calculations.basicCalculationModal.createColumnFailureToastHeader,
            bodyText: localeText.calculations.basicCalculationModal.createColumnFailureToastMessage,
            dataTest: `toast-calculated-column-failue`
          });
        });
      modalActionCreators.resetModalState();
      basicCalculationActionCreators.resetBasicCalculation();
    }
  })
);

export const selectCreateConditionalModalState = createSelector(
  reportLocaleText,
  selectLocaleFormatter,
  selectIfColumnOptions,
  selectValueToCompareOptions,
  selectOptionalSecondValueToCompareOptions,
  selectDetailTableColumns,
  selectConditional,
  selectConditionalOperatorOptions,
  selectCurrentConditionSetup,
  selectConditionalPreviewTableProps,
  (
    localeText,
    localeFormatter,
    ifColumnOptions,
    valueToCompareOptions,
    optionalSecondValueToCompareOptions,
    columns,
    conditional,
    conditionalOptions,
    currentConditionSetup,
    conditionalPreviewTableProps
  ): ICreateConditionalModal => ({
    reportLocaleText: localeText,
    localeFormatter,
    ifColumnOptions,
    valueToCompareOptions,
    optionalSecondValueToCompareOptions,
    columns,
    conditional,
    conditionalOptions,
    currentConditionSetup,
    conditionalPreviewTableProps,
    closeModal: () => {
      modalActionCreators.resetModalState();
      conditionalActionCreators.resetConditional();
    },
    goBack: () => {
      modalActionCreators.setModalComponentType(ReportModalTypes.CREATE_CALCULATION);
      conditionalActionCreators.resetConditional();
    },
    addCondition: (value) => conditionalActionCreators.addCondition(value),
    fetchPreviewData: async () => conditionalEffects.getConditionalPreviewTableData(),
    updateColumnType: (columnType) => conditionalActionCreators.updateSelectedColumnType(columnType),
    updateIfValue: (column: IRecordOf<IReportColumn>) => conditionalActionCreators.updateIfValue(column),
    updateCurrentConditionOperator: (operator) => conditionalActionCreators.updateCurrentConditionOperator(operator),
    updateCurrentConditionMatchValue: (value) => conditionalActionCreators.updateCurrentConditionMatchValue(value),
    updateCurrentConditionLowerBound: (value) => conditionalActionCreators.updateCurrentConditionLowerBound(value),
    updateCurrentConditionUpperBound: (value) => conditionalActionCreators.updateCurrentConditionUpperBound(value),
    updateCurrentConditionReturnedValue: (value) =>
      conditionalActionCreators.updateCurrentConditionReturnedValue(value),
    updateCurrentConditionCustomValue: ({ value, valueType }) =>
      conditionalActionCreators.updateCurrentConditionCustomValue({ value, valueType }),
    updateElseValue: (value) => conditionalActionCreators.updateElseResult(value),
    updateColumnName: (name: string) => conditionalActionCreators.updateColumnName(name),
    saveColumn: (column: IConditionalReportColumn<any>, navigate?: NavigateFunction) => {
      reportConfigurationActionCreators.addConditionalColumnToDetailTable({ newColumn: column });
      reportConfigurationEffects.createReportConfigurationAndUpdateUrlWithToast({
        successHeader: localeText.calculations.conditionalModal.conditionalSuccessToastHeader,
        successMessage: localeText.calculations.conditionalModal.conditionalSuccessToastMessage,
        failHeader: localeText.calculations.conditionalModal.conditionalFailureToastHeader,
        failMessage: localeText.calculations.conditionalModal.conditionalFailureToastMessage,
        navigate
      });
      modalActionCreators.resetModalState();
      conditionalActionCreators.resetConditional();
    }
  })
);

export const selectSavedViewCreatingModalStateHandler =
  (
    rActionCreators: typeof reportActionCreators,
    mActionCreators: typeof modalActionCreators,
    sViewEffects: typeof savedViewEffects
  ) =>
  (localeText, showColumn, organizedBy, categorizedBy, entity, findColumnDisplayName, savedViews: ISavedView[]) => ({
    showColumnName: findColumnDisplayName(showColumn),
    organizedBy: organizedBy,
    categorizedBy: categorizedBy.displayName,
    reportLocaleText: localeText,
    existingSavedViews: savedViews,
    onClose: () => mActionCreators.resetModalState(),
    onCreate: async (titleAndDescription: ISavedViewTitleAndDescription, navigate: NavigateFunction) => {
      mActionCreators.resetModalState();
      return sViewEffects
        .createSavedView(titleAndDescription)
        .then((newSavedView) => {
          reportHistoryActionCreators.clearReportHistory();
          return newSavedView;
        })
        .then((newSavedView) => {
          // Not an open redirect: this is a client-side react-router navigation to a relative
          // in-app path built by reportSavedViewSuffix() from the resolved entity id and
          // server-returned saved-view/configuration UUIDs. No user-supplied URL reaches it.
          navigate(reportSavedViewSuffix(entity.id, newSavedView.reportConfiguration, newSavedView)); // legit:ignore-sast
        })
        .catch((error: unknown) => {
          // A failed config persist already showed a permission-specific toast; showing the
          // generic create-failed toast here would overwrite (mask) it. See BUD-17949.
          const configPersistFailed = (error as { reportConfigurationPersistFailed?: boolean })
            ?.reportConfigurationPersistFailed;
          if (configPersistFailed) {
            return;
          }
          rActionCreators.toggleMuiToast({
            type: MuiToastTypes.FAILURE,
            headerText: localeText.savedView.modal.createFailedHeader,
            bodyText: localeText.savedView.modal.createFailedBody
          });
        });
    }
  });

export const selectReportDeletingModalStateHandler =
  (
    mActionCreators: typeof modalActionCreators,
    rEffects: typeof reportEffects & { deleteReport: (args: { report: IReportResponse }) => Promise<void> }
  ) =>
  (localeText, report: IReport, entity: IEntity, isModalOpen: boolean) => ({
    reportLocaleText: localeText,
    report: report,
    onClose: () => mActionCreators.resetModalState(),
    onDelete: (r: IReportResponse, navigate: NavigateFunction, toggleToast?: (toastProps: ToastProps) => void) => {
      return rEffects
        .deleteReport({ report: r })
        .then(() => {
          void queryClient.invalidateQueries({ queryKey: foldersAndReportsQueryKeys.all });
          navigate(reportGalleryPathReactRouterVersion(entity.id));
        })
        .then(() => {
          if (toggleToast) {
            toggleToast({
              severity: 'success',
              title: `${r.title} ${localeText.reportExplorer.delete.suceededMessage}`,
              dataTest: 'report-delete-toast'
            });
          }
        })
        .catch(() => {
          if (toggleToast) {
            toggleToast({
              severity: 'error',
              title: localeText.reportExplorer.delete.failedHeader,
              body: localeText.reportExplorer.delete.failedBody,
              dataTest: 'report-delete-toast'
            });
          }
        })
        .finally(mActionCreators.resetModalState);
    },
    open: isModalOpen
  });

export const selectReportDeletingModalState = createSelector(
  reportLocaleText,
  selectCurrentReport,
  selectCurrentEntity,
  selectIsModalOpen,
  selectReportDeletingModalStateHandler(modalActionCreators, reportEffects)
);

export const selectSavedViewCreatingModalState = createSelector(
  reportLocaleText,
  selectShowColumn,
  currentOrganizedBySelectors.selectLabelForOrganizedByOption,
  selectCategorizedByColumn,
  selectCurrentEntity,
  selectColumnDisplayName,
  selectSavedViews,
  selectSavedViewCreatingModalStateHandler(reportActionCreators, modalActionCreators, savedViewEffects)
);

export const selectSavedViewDeletingModalState = createSelector(
  reportLocaleText,
  selectCurrentSavedViewEditSavedView,
  getEntitySubdomain,
  selectCurrentReport,
  (localeText, savedView, subdomain, report) => ({
    reportLocaleText: localeText,
    savedView,
    onClose: () => modalActionCreators.resetModalState(),
    onDelete: (sv: ISavedView, navigate: NavigateFunction) => {
      modalActionCreators.resetModalState();

      savedViewEffects
        .deleteSavedView({ savedView: sv })
        .then(() => {
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.SUCCESS,
            headerText: `${sv.title} ${localeText.savedView.modal.deleteSuceededMessage}`
          });
          navigate(
            reportSavedViewsPathReactRouterVersion(subdomain, {
              reportId: sv.reportId
            })
          );
          return reportConfigurationEffects
            .getReportConfiguration({ reportConfigurationId: report.defaultReportConfiguration })
            .then((reportConfiguration) => {
              reportConfigurationActionCreators.setCurrentReportConfiguration(reportConfiguration);
            })
            .catch(() => {
              reportActionCreators.toggleMuiToast({
                type: MuiToastTypes.FAILURE,
                headerText: localeText.defaultReportConfiguration.fetchFailedHeader,
                bodyText: localeText.defaultReportConfiguration.fetchFailedBody
              });
            });
        })
        .catch(() => {
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.FAILURE,
            headerText: localeText.savedView.modal.deleteFailedHeader,
            bodyText: localeText.savedView.modal.deleteFailedBody
          });
        });
    }
  })
);

export const selectReportExportModalState = createSelector(
  reportLocaleText,
  selectRouteName,
  selectCurrentViewExportable,
  selectCurrentViewMaxExportRows,
  selectLocaleFormatter,
  (localeText, routeName, exportable, maxExportRows, localFormatter) => ({
    exportReport: (filter: Filter) =>
      routeName === 'ReportSavedViewEdit'
        ? reportEffects.exportReportFromSavedViewEdit(filter === `currentFilters`)
        : reportEffects.exportReportFromReportExplorer(filter === `currentFilters`),
    isFiltersDisabled: !exportable,
    formattedMaxExportRows: maxExportRows ? localFormatter.number({ value: maxExportRows }) : '',
    reportLocaleText: localeText,
    onClose: () => modalActionCreators.resetModalState()
  })
);

const selectActiveColumn = createSelector(
  selectColumnByNameFromCurrentView,
  selectSelectedAgGridColumn,
  (getActiveColumnByName, selectedColumnName) => getActiveColumnByName(selectedColumnName)
);

const selectOriginalActiveColumnName = createSelector(
  selectSelectedAgGridColumn,
  selectColumnAliasesFromDataMgr,
  (activeColumn, dataMgrColumnAliases) => {
    const tableColumn = dataMgrColumnAliases.find((col) => col.originalName === activeColumn);
    return tableColumn?.displayName;
  }
);

export const selectDependentColumns = createSelector(
  selectActiveColumn,
  selectDetailTableColumns,
  (activeColumn, allColumns) =>
    allColumns.filter(
      (c) =>
        (c.columnType === EReportColumnTypes.infix &&
          (c.leftColumn.name === activeColumn.name || c.rightColumn.name === activeColumn.name)) ||
        (c.columnType === EReportColumnTypes.conditional && c.sourceColumn.name === activeColumn.name)
    )
);

const selectDeletionBlockingReportControls = createSelector(
  selectActiveColumn,
  selectShowColumn,
  currentOrganizedBySelectors.selectOrganizedByColumn,
  selectCategorizedByColumn,
  selectCurrentFilters,
  selectDependentColumns,
  (column, show, org, category, filters, dependentColumns): IDeletionBlockingReportControls => {
    return {
      isShownBy: show.name === column.name,
      isOrganizedBy: org.name === column.name,
      isCategorizedBy: category.name === column.name,
      isFiltered:
        filters.find((f) => {
          return f.columnName === column.name;
        }) !== undefined,
      dependentColumns
    };
  }
);

export const selectColumnSettingsModalState = createSelector(
  reportLocaleText,
  selectHiddenControls,
  selectColumnNames,
  selectActiveColumn,
  selectDeletionBlockingReportControls,
  selectCategorizedByColumn,
  selectOriginalActiveColumnName,
  (
    localText,
    hidden,
    aliases,
    activeColumn,
    deletionBlockingReportControls,
    categorizedByColumn,
    originalColumnName
  ): IColumnSettingsModal => {
    return {
      aliases: aliases,
      allHidden: hidden,
      column: activeColumn,
      deletionBlockingReportControls,
      oversizeCount: 1000,
      reportLocaleText: localText,
      originalColumnName,
      onClose: () => modalActionCreators.resetModalState(),
      onDelete: (column: IReportColumn) => {
        reportConfigurationActionCreators.removeColumnFromDetailTable({ columnNameToDelete: column.name });
        modalActionCreators.resetModalState();
        reportConfigurationEffects
          .createReportConfigurationAndUpdateUrl()
          .then(() => {
            reportActionCreators.toggleMuiToast({
              type: MuiToastTypes.SUCCESS,
              headerText: `${column.displayName} ${localText.calculations.deleteCalculationModal.deleteCreatedColumnSucessToastHeader}`,
              dataTest: `toast-deleted-created-column-sucess`
            });
          })
          .catch(() => {
            reportActionCreators.toggleMuiToast({
              type: MuiToastTypes.FAILURE,
              headerText: localText.calculations.deleteCalculationModal.deleteCreatedColumnFailureToastHeader,
              bodyText: localText.calculations.deleteCalculationModal.deleteCreatedColumnFailureToastBody,
              dataTest: `toast-deleted-created-column-failure`
            });
          });
      },
      onSave: (columnName: string, displayName: string, types: any) => {
        const newColumn = produce(activeColumn, (newState) => {
          newState.displayName = displayName;
        });
        const hiddenTypes = types;
        reportConfigurationActionCreators.setDisplayName({ column: columnName, displayName: displayName });
        reportConfigurationActionCreators.setHiddenControls({ column: columnName, types: hiddenTypes });
        if (categorizedByColumn.name === activeColumn.name) {
          reportConfigurationActionCreators.setVisualizationQueryCategorizedByColumn(newColumn);
        }
        reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
        modalActionCreators.resetModalState();
      }
    };
  }
);

export const selectColorControlsModalState = createSelector(
  reportLocaleText,
  selectSavedViewEditActiveCategory,
  (localeText, activeCategory) => ({
    reportLocaleText: localeText,
    color: activeCategory.color,
    onClose: () => modalActionCreators.resetModalState(),
    onSave: (color: string) => {
      reportConfigurationActionCreators.setCategoryColor({ category: activeCategory.name, color });
      reportSavedViewEditActionCreators.setActiveCategory({
        name: activeCategory.name,
        color
      });
      reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
      modalActionCreators.resetModalState();
    }
  })
);

export const selectSetTargetModalStateHandler =
  (
    mActionCreators: typeof modalActionCreators,
    tActionCreators: typeof reportTargetActionCreators,
    rcActionCeators: typeof reportConfigurationActionCreators,
    _rActionCreators: typeof reportActionCreators
  ) =>
  (
    localeText,
    showColumn,
    localeFormatter,
    targetCondition,
    secondTargetCondition,
    targetElseStatus,
    hasTarget: boolean
  ): ISetTargetModalProps => ({
    reportLocaleText: localeText,
    showColumnType: showColumn.type,
    localeFormatter,
    targetCondition,
    secondTargetCondition,
    targetElseStatus,
    isEditingTarget: hasTarget,
    updateConditionOperator: (operator) => tActionCreators.updateConditionOperator(operator),
    updateConditionValue: (value) => tActionCreators.updateConditionValue(value),
    updateConditionStatusText: (text) => tActionCreators.updateConditionStatusText(text),
    updateSecondConditionValue: (value) => tActionCreators.updateSecondConditionValue(value),
    updateSecondConditionStatusText: (text) => tActionCreators.updateSecondConditionStatusText(text),
    updateElseStatusText: (text) => tActionCreators.updateElseStatusText(text),
    onClose: () => {
      tActionCreators.resetTargetState();
      modalActionCreators.resetModalState();
    },
    onSave: (target: IReportTarget) => {
      rcActionCeators.setTarget(target);
      reportConfigurationEffects
        .createReportConfigurationAndUpdateUrl()
        .then(() => {
          tActionCreators.resetTargetState();
          mActionCreators.resetModalState();
        })
        .catch(() => {
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.FAILURE,
            headerText: localeText.setTargetModal.failureToast.title,
            bodyText: localeText.setTargetModal.failureToast.description,
            dataTest: 'toast-set-target-failure'
          });
        });
    },
    onClickRemove: () => {
      mActionCreators.resetModalState();
      rcActionCeators.resetTarget();
      reportConfigurationEffects.createReportConfigurationAndUpdateUrl().then(() => {
        tActionCreators.resetTargetState();
      });
    }
  });

export const selectSetTargetModalState = createSelector(
  reportLocaleText,
  selectShowColumn,
  selectLocaleFormatter,
  selectReportTargetCondition,
  selectSecondReportTargetCondition,
  selectReportTargetElseStatus,
  selectHasReportTarget,
  selectSetTargetModalStateHandler(
    modalActionCreators,
    reportTargetActionCreators,
    reportConfigurationActionCreators,
    reportActionCreators
  )
);

export const selectShareModalState = createSelector(reportLocaleText, shareLinkSelector, (localeText, share) => ({
  reportLocaleText: localeText,
  snapshotLink: share.snapshotLink,
  permalinkLink: share.permalinkLink,
  isSnapshotLinkCopied: share.isSnapshotLinkCopied,
  isPermalinkLinkCopied: share.isPermalinkLinkCopied,
  closeModal: () => {
    modalActionCreators.resetModalState();
    shareLinkActionCreators.setSnapshotLinkCopied(false);
    shareLinkActionCreators.setPermalinkLinkCopied(false);
  },
  onSnapshotCopy: () => {
    shareLinkActionCreators.setSnapshotLinkCopied(true);
    shareLinkActionCreators.setPermalinkLinkCopied(false);
  },
  onPermalinkCopy: () => {
    shareLinkActionCreators.setPermalinkLinkCopied(true);
    shareLinkActionCreators.setSnapshotLinkCopied(false);
  }
}));

export const selectManageModalState = createSelector(
  reportLocaleText,
  selectCurrentReportTitle,
  selectCurrentGrantees,
  selectTemporaryGrantees,
  selectInvitableUsers,
  getSessionUUID,
  getNumericalEntityId,
  selectIsReportMultiDataset,
  selectIsModalOpen,
  (
    localeText,
    reportTitle,
    currentGrantees,
    temporaryGrantees,
    invitableUsers,
    userUuid,
    entityId,
    isReportMultiDataset,
    isModalOpen
  ) => ({
    reportLocaleText: localeText,
    reportTitle,
    currentGrantees,
    temporaryGrantees,
    invitableUsers,
    currentUserId: userUuid,
    entityId,
    updateTemporaryGrantee: (grantee: IGrantee) => accessTableActionCreators.updateTemporaryGrantee(grantee),
    saveAccess: (updatedGrantees: Map<string, IGrantee>) => {
      void reportAccessEffects.updateReportGrantees(updatedGrantees);
    },
    closeModal: () => {
      modalActionCreators.resetModalState();
    },
    isReportMultiDataset,
    isModalOpen
  })
);

export const selectStandardTemplateModalState = createSelector(
  reportLocaleText,
  selectCurrentReport,
  (localeText, currentReport) => ({
    reportLocaleText: localeText,
    currentReport,
    closeModal: () => {
      modalActionCreators.resetModalState();
    }
  })
);

export const selectCopyReportModalState = createSelector(
  reportLocaleText,
  selectNewReport,
  selectCurrentReport,
  selectCurrentTemplate,
  selectIsReportDimensional,
  selectNewReportFormDescriptionValidationState,
  selectNewReportFormTitleValidationState,
  selectNewReportFormValidationState,
  selectNewReportTypeState,
  getNumericalEntityId,
  (
    localeText,
    newReport,
    currentReport,
    template,
    isDimensional: boolean,
    descriptionState,
    titleState,
    formState,
    reportType,
    entityId
  ) => ({
    newReport: newReport,
    reportLocaleText: localeText,
    currentReport: currentReport,
    currentTemplate: template,
    titleState,
    descriptionValidationState: descriptionState,
    typeState: reportType,
    formState,
    entityId,
    // Standard report templates have no entry in the reports collection, so
    // `currentReport` is undefined and the copy has no source folder.
    sourceParentFolderId: currentReport?.parentFolderId?.toString() ?? 'none',
    closeModal: () => {
      modalActionCreators.resetModalState();
    },
    onCopy: (
      copySavedViews: boolean,
      copyAccess: ValidationStates,
      navigate: NavigateFunction,
      parentFolderId?: string | null
    ) => {
      // we are in a standard template
      if (template) {
        reportEffects.createCustomReportFromStandardAndRedirect({
          isDimensional: isDimensional || false,
          copySavedViews: copySavedViews,
          copyAccess: copyAccess,
          templateSavedViews: template?.savedViews,
          parentFolderId
        });
      } else {
        reportEffects.createCustomReportAndRedirect({ copySavedViews, navigate, parentFolderId });
      }
    },
    onReportTitleBlur: (reportTitle) => reportCreateActionCreators.blurReportTitle(reportTitle),
    onReportDescriptionBlur: (reportDescription) => reportCreateActionCreators.blurReportDescription(reportDescription),
    onReportTitleChange: (reportTitle) => reportCreateActionCreators.updateReportTitle(reportTitle),
    onReportDescriptionChange: (reportDescription) =>
      reportCreateActionCreators.updateReportDescription(reportDescription)
  })
);

const updateControlBlockModalState = () => {
  dimensionalTableActionCreators.updateControlBlockStatus(ControlBlockStatus.Current);
  modalActionCreators.resetModalState();
};

export const selectControlBlockModalState = createSelector(
  selectControlBlockProps,
  reportLocaleText,
  (controlBlockModalState, reportText) => {
    return {
      closeModal: () => {
        modalActionCreators.resetModalState();
      },
      updateControlBlockName: (payload) => {
        const { controlBlockIndex, displayName } = payload;
        reportConfigurationActionCreators.updateOrAddDimensionalSummaryBlockNames(payload);
        dimensionalTableActionCreators.updateControlBlockStatus(ControlBlockStatus.Updating);
        reportConfigurationEffects
          .createReportConfigurationAndUpdateUrl()
          .then(() => {
            reportExplorerActionCreators.updateDimensionalGroupData({
              rowIndex: controlBlockIndex,
              displayName
            });
            updateControlBlockModalState();
          })
          .catch(updateControlBlockModalState);
      },
      controlBlockDetails: controlBlockModalState,
      reportText
    };
  }
);
